import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading } from "../components/common";
import { gameLabel } from "./Dashboard";
import { toast } from "sonner";
import { Trash2, PlayCircle, Trophy, Ban } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "in_progress", label: "In Progress" },
  { key: "abandoned", label: "Abandoned" },
];

export default function MatchHistory() {
  const [matches, setMatches] = useState(null);
  const [filter, setFilter] = useState("all");
  const { isOwner } = useAuth();
  const navigate = useNavigate();

  const load = async () => setMatches((await api.get("/matches")).data);
  useEffect(() => {
    load();
  }, []);

  const remove = async (m, e) => {
    e.stopPropagation();
    if (
      !window.confirm(
        "Delete this match permanently? All affected player statistics will automatically recalculate."
      )
    )
      return;
    await api.delete(`/matches/${m.id}`);
    toast.success("Match deleted — stats recalculated");
    load();
  };

  if (matches === null) return <Loading label="Loading history" />;

  const filtered = filter === "all" ? matches : matches.filter((m) => m.status === filter);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">
          Source of Truth
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          Match History
        </h1>
      </div>

      <div className="flex gap-1 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors duration-150 ${
              filter === f.key ? "bg-[#007AFF] text-white" : "panel text-slate-400 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-slate-500">No matches here.</p>
      ) : (
        <div className="space-y-1">
          {filtered.map((m) => (
            <div
              key={m.id}
              data-testid={`history-match-${m.id}`}
              onClick={() => navigate(`/match/${m.id}`)}
              className="panel rounded-sm p-4 flex items-center gap-4 cursor-pointer hover:border-[#007AFF] transition-colors duration-150"
            >
              <StatusIcon status={m.status} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  {gameLabel(m.game_type)} • Best of {m.best_of} •{" "}
                  {new Date(m.date).toLocaleDateString()}
                </div>
                <div className="font-display text-lg font-bold text-white truncate">
                  {Object.values(m.player_names).join("  vs  ")}
                </div>
              </div>
              {m.status === "completed" && (
                <div className="text-right">
                  <div className="font-display text-2xl font-black text-white">
                    {m.player_ids.map((id) => (m.final_score || {})[id] ?? 0).join(" - ")}
                  </div>
                  <div className="text-xs font-bold uppercase text-[#FFCC00]">
                    {m.player_names[m.winner_id]} won
                  </div>
                </div>
              )}
              {m.status === "in_progress" && (
                <span className="text-xs font-bold uppercase text-[#FFCC00]">Resume</span>
              )}
              <button
                data-testid={`delete-match-${m.id}`}
                onClick={(e) => remove(m, e)}
                className={`text-slate-600 hover:text-[#FF3B30] p-2 transition-colors ${
                  isOwner ? "" : "hidden"
                }`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "completed")
    return (
      <div className="w-10 h-10 flex items-center justify-center bg-[#0d1f12] rounded-sm shrink-0">
        <Trophy className="w-5 h-5 text-[#34C759]" />
      </div>
    );
  if (status === "in_progress")
    return (
      <div className="w-10 h-10 flex items-center justify-center bg-[#2b2410] rounded-sm shrink-0">
        <PlayCircle className="w-5 h-5 text-[#FFCC00]" />
      </div>
    );
  return (
    <div className="w-10 h-10 flex items-center justify-center bg-[#241010] rounded-sm shrink-0">
      <Ban className="w-5 h-5 text-[#FF3B30]" />
    </div>
  );
}
